import { UserScreenId } from "@vahak/core/dist/constants/userScreenId";
import {
    BANK_DETAILS,
    DASHBOARD_ROUTE,
    HOME_ROUTE,
    LISTING_PAGE,
    CLIENT_LISTING_PAGE,
    MARKETPLACE_ROUTE,
    MEMBERSHIP,
    MEMBERSHIP_HISTORY,
    MEMBERSHIP_UPGRADE,
    MY_LORRIES_PAGE,
    TRANSPORT_DIRECTORY,
    TRANSPORT_NAGAR,
    USER_PROFILE_PAGE
} from "../constants/route-constants";

/** TopNavV2 labels, profile menu, app download banners. */
export const WEB_V2_NAV_LOCALIZATION_NAMESPACES = ["navigation", "common"] as const;

/**
 * Compact post / bid sheets. SSR only if the page shows this copy above the fold.
 * Providers always load these on open; leaf UI loads them again as a backup.
 */
export const WEB_V2_POSTING_LOCALIZATION_NAMESPACES = ["load-form", "homepageV2", "mktCardV2", "common"] as const;

export const WEB_V2_BIDDING_LOCALIZATION_NAMESPACES = WEB_V2_POSTING_LOCALIZATION_NAMESPACES;

/** Logged-in chrome: TopNavV2. Posting/bidding strings load when those sheets open. */
export const LOGGED_IN_APP_LOCALIZATION_NAMESPACES = WEB_V2_NAV_LOCALIZATION_NAMESPACES;

function withNav<const T extends readonly string[]>(...pageNs: T) {
    return [...pageNs, ...WEB_V2_NAV_LOCALIZATION_NAMESPACES] as const;
}

/** Homepage first paint (hero, cards, nav). Compact post/bid load their own ns on open. */
export const HOME_LOCALIZATION_NAMESPACES = withNav("homepageV2", "mktCardV2");

/** Marketplace V3 first paint. Bid/post sheets load load-form on open. */
export const MARKETPLACE_V3_LOCALIZATION_NAMESPACES = withNav("marketplaceV3", "homepageV2", "mktCardV2");

export const MEMBERSHIP_UPGRADE_LOCALIZATION_NAMESPACES = withNav("membershipPage");

export const DASHBOARD_LOCALIZATION_NAMESPACES = withNav("homeRevamp");

export const LISTING_PAGE_LOCALIZATION_NAMESPACES = withNav("marketplace", "userProfile");

export const USER_PROFILE_LOCALIZATION_NAMESPACES = withNav("userProfile");

export const BANK_DETAILS_LOCALIZATION_NAMESPACES = WEB_V2_NAV_LOCALIZATION_NAMESPACES;

export const MEMBERSHIP_PLAN_LOCALIZATION_NAMESPACES = withNav("membershipPage");

export const TRANSPORT_DIRECTORY_LOCALIZATION_NAMESPACES = withNav("transporters");

export const TRANSPORTER_PROFILE_LOCALIZATION_NAMESPACES = withNav("userProfile", "marketplace", "transporters");

export const TRANSPORT_NAGAR_LOCALIZATION_NAMESPACES = withNav("mktCardV2");

export const SERVICE_PAGE_LOCALIZATION_NAMESPACES = withNav("servicePages");

export const WHY_CHOOSE_US_LOCALIZATION_NAMESPACES = withNav("whyChooseUs");

export const HOW_VAHAK_HELPS_LOCALIZATION_NAMESPACES = withNav("howVahakHelpYou");

/** City / LCV SEO landings — page copy only; compact post loads on CTA. */
export const CITY_PAGE_LOCALIZATION_NAMESPACES = withNav("lcvRouteLanding");

export const LCV_ROUTE_BOOKING_LOCALIZATION_NAMESPACES = withNav("lcvRouteLanding");

export const FAQ_LOCALIZATION_NAMESPACES = withNav("faq");

export const CONTACT_US_LOCALIZATION_NAMESPACES = withNav("contactus");

export const CAREERS_LOCALIZATION_NAMESPACES = WEB_V2_NAV_LOCALIZATION_NAMESPACES;

export const ABOUT_US_LOCALIZATION_NAMESPACES = WEB_V2_NAV_LOCALIZATION_NAMESPACES;

export const PAYMENT_LOCALIZATION_NAMESPACES = WEB_V2_NAV_LOCALIZATION_NAMESPACES;

export const HELP_LOCALIZATION_NAMESPACES = ["help", "common"] as const;

export const STATIC_PAGE_LOCALIZATION_NAMESPACES = WEB_V2_NAV_LOCALIZATION_NAMESPACES;

export const VAS_STATIC_LOCALIZATION_NAMESPACES = WEB_V2_NAV_LOCALIZATION_NAMESPACES;

export const CARGO_INSURANCE_LOCALIZATION_NAMESPACES = withNav("cargoInsurance");

/**
 * Maps a route pathname or layout route id to the i18n namespace files
 * required by `serverSideTranslations`.
 */
export const ROUTE_LOCALIZATION_NAMESPACES = {
    "/": HOME_LOCALIZATION_NAMESPACES,
    [HOME_ROUTE]: HOME_LOCALIZATION_NAMESPACES,
    [MARKETPLACE_ROUTE]: MARKETPLACE_V3_LOCALIZATION_NAMESPACES,
    "/membership/upgrade": MEMBERSHIP_UPGRADE_LOCALIZATION_NAMESPACES,
    [MEMBERSHIP_UPGRADE]: MEMBERSHIP_UPGRADE_LOCALIZATION_NAMESPACES,
    [DASHBOARD_ROUTE]: DASHBOARD_LOCALIZATION_NAMESPACES,
    "/dashboard": DASHBOARD_LOCALIZATION_NAMESPACES,
    [LISTING_PAGE]: LISTING_PAGE_LOCALIZATION_NAMESPACES,
    [BANK_DETAILS]: BANK_DETAILS_LOCALIZATION_NAMESPACES,
    [MEMBERSHIP]: MEMBERSHIP_PLAN_LOCALIZATION_NAMESPACES,
    [MEMBERSHIP_HISTORY]: MEMBERSHIP_PLAN_LOCALIZATION_NAMESPACES,
    [MY_LORRIES_PAGE]: USER_PROFILE_LOCALIZATION_NAMESPACES,
    new: USER_PROFILE_LOCALIZATION_NAMESPACES,
    [USER_PROFILE_PAGE]: USER_PROFILE_LOCALIZATION_NAMESPACES,
    [TRANSPORT_DIRECTORY]: TRANSPORT_DIRECTORY_LOCALIZATION_NAMESPACES,
    transporterProfile: TRANSPORTER_PROFILE_LOCALIZATION_NAMESPACES,
    [TRANSPORT_NAGAR]: TRANSPORT_NAGAR_LOCALIZATION_NAMESPACES,
    servicePages: SERVICE_PAGE_LOCALIZATION_NAMESPACES,
    whyChooseUs: WHY_CHOOSE_US_LOCALIZATION_NAMESPACES,
    howVahakHelpYou: HOW_VAHAK_HELPS_LOCALIZATION_NAMESPACES,
    cityPages: CITY_PAGE_LOCALIZATION_NAMESPACES,
    faq: FAQ_LOCALIZATION_NAMESPACES,
    contactus: CONTACT_US_LOCALIZATION_NAMESPACES,
    careers: CAREERS_LOCALIZATION_NAMESPACES,
    aboutUs: ABOUT_US_LOCALIZATION_NAMESPACES,
    [UserScreenId.payment.id]: PAYMENT_LOCALIZATION_NAMESPACES,
    help: HELP_LOCALIZATION_NAMESPACES,
    staticPage: STATIC_PAGE_LOCALIZATION_NAMESPACES,
    [UserScreenId.cargoInsurance.pathname]: CARGO_INSURANCE_LOCALIZATION_NAMESPACES,
    [UserScreenId.cargoInsurance.id]: CARGO_INSURANCE_LOCALIZATION_NAMESPACES,
    "/vas/commercial-vehicle-insurance": VAS_STATIC_LOCALIZATION_NAMESPACES,
    [UserScreenId.lcvBooking.pathname]: LCV_ROUTE_BOOKING_LOCALIZATION_NAMESPACES,
    [UserScreenId.lcvBooking.id]: LCV_ROUTE_BOOKING_LOCALIZATION_NAMESPACES,
    [CLIENT_LISTING_PAGE]: LISTING_PAGE_LOCALIZATION_NAMESPACES,
    [UserScreenId.listing.pathname]: LISTING_PAGE_LOCALIZATION_NAMESPACES
} as const;

export type RouteLocalizationKey = keyof typeof ROUTE_LOCALIZATION_NAMESPACES;

export function getRouteLocalizationNamespaces(route: string): string[] {
    const namespaces = ROUTE_LOCALIZATION_NAMESPACES[route as RouteLocalizationKey];

    if (namespaces) {
        return [...namespaces];
    }

    return [...WEB_V2_NAV_LOCALIZATION_NAMESPACES];
}
